import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { IonIcon } from '@ionic/react';
import { locationOutline, close } from 'ionicons/icons';

interface CitySuggestion {
  id: number;
  name: string;
  county: string;
  displayName: string;
}

interface CityAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
}

const DEBOUNCE_MS = 350;
const MIN_CHARS = 2;

async function searchCities(query: string, signal?: AbortSignal): Promise<CitySuggestion[]> {
  const params = new URLSearchParams({
    q: query,
    format: 'json',
    addressdetails: '1',
    limit: '6',
    countrycodes: 'ro',
    'accept-language': 'ro',
    featuretype: 'city',
  });

  const res = await fetch(`https://nominatim.openstreetmap.org/search?${params}`, { signal });
  if (!res.ok) return [];

  const data = await res.json();
  const seen = new Set<string>();

  return data
    .filter((r: { addresstype: string }) =>
      ['city', 'town', 'village', 'municipality'].includes(r.addresstype),
    )
    .map((r: { place_id: number; address: { city?: string; town?: string; village?: string; municipality?: string; county?: string }; display_name: string }) => {
      const name = r.address.city || r.address.town || r.address.village || r.address.municipality || r.display_name.split(',')[0];
      const county = r.address.county || '';
      const key = `${name}-${county}`.toLowerCase();
      if (seen.has(key)) return null;
      seen.add(key);
      return {
        id: r.place_id,
        name,
        county,
        displayName: county ? `${name}, ${county}` : name,
      } as CitySuggestion;
    })
    .filter(Boolean) as CitySuggestion[];
}

const CityAutocomplete: React.FC<CityAutocompleteProps> = ({
  value,
  onChange,
  placeholder = 'ex: Arad',
  className,
}) => {
  const [inputValue, setInputValue] = useState(value);
  const [suggestions, setSuggestions] = useState<CitySuggestion[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(-1);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0, width: 0, openAbove: false });

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const abortRef = useRef<AbortController | null>(null);
  const isFocusedRef = useRef(false);

  useEffect(() => {
    if (!isFocusedRef.current) setInputValue(value);
  }, [value]);

  const updateDropdownPos = useCallback(() => {
    if (wrapperRef.current) {
      const rect = wrapperRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const openAbove = spaceBelow < 240;
      setDropdownPos({ top: openAbove ? rect.top - 4 : rect.bottom + 4, left: rect.left, width: rect.width, openAbove });
    }
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (wrapperRef.current && !wrapperRef.current.contains(target)) {
        const dropdown = document.getElementById('city-dropdown-portal');
        if (dropdown && dropdown.contains(target)) return;
        setIsOpen(false);
      }
    };
    const handleScroll = () => { if (isOpen) updateDropdownPos(); };
    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('scroll', handleScroll, true);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [isOpen, updateDropdownPos]);

  const fetchSuggestions = useCallback(async (query: string) => {
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setIsLoading(true);
    try {
      const data = await searchCities(query, controller.signal);
      setSuggestions(data);
      if (data.length > 0) { updateDropdownPos(); setIsOpen(true); }
      else setIsOpen(false);
      setHighlightIndex(-1);
    } catch (err) {
      if ((err as Error).name !== 'AbortError') { setSuggestions([]); setIsOpen(false); }
    } finally {
      setIsLoading(false);
    }
  }, [updateDropdownPos]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setInputValue(val);
    onChange(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (val.length < MIN_CHARS) { setSuggestions([]); setIsOpen(false); return; }
    debounceRef.current = setTimeout(() => { void fetchSuggestions(val); }, DEBOUNCE_MS);
  };

  const handleSelect = (city: CitySuggestion) => {
    setInputValue(city.name);
    setIsOpen(false);
    setSuggestions([]);
    onChange(city.name);
  };

  const handleClear = () => {
    setInputValue('');
    setSuggestions([]);
    setIsOpen(false);
    onChange('');
    inputRef.current?.focus();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen || suggestions.length === 0) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setHighlightIndex((p) => Math.min(p + 1, suggestions.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHighlightIndex((p) => Math.max(p - 1, 0)); }
    else if (e.key === 'Enter' && highlightIndex >= 0 && suggestions[highlightIndex]) { e.preventDefault(); handleSelect(suggestions[highlightIndex]); }
    else if (e.key === 'Escape') setIsOpen(false);
  };

  const dropdown = isOpen && suggestions.length > 0
    ? createPortal(
        <ul
          id="city-dropdown-portal"
          className="address-autocomplete__dropdown"
          style={{
            position: 'fixed',
            ...(dropdownPos.openAbove
              ? { bottom: window.innerHeight - dropdownPos.top, top: 'auto' }
              : { top: dropdownPos.top }),
            left: dropdownPos.left,
            width: dropdownPos.width,
          }}
        >
          {suggestions.map((item, idx) => (
            <li
              key={item.id}
              className={`address-autocomplete__item${idx === highlightIndex ? ' address-autocomplete__item--active' : ''}`}
              onMouseEnter={() => setHighlightIndex(idx)}
              onMouseDown={(e) => { e.preventDefault(); handleSelect(item); }}
            >
              <IonIcon icon={locationOutline} className="address-autocomplete__item-icon" />
              <div className="address-autocomplete__item-text">
                <span className="address-autocomplete__item-main">{item.name}</span>
                {item.county && <span className="address-autocomplete__item-secondary">{item.county}</span>}
              </div>
            </li>
          ))}
        </ul>,
        document.body,
      )
    : null;

  return (
    <div ref={wrapperRef} className={`address-autocomplete ${className ?? ''}`}>
      <div className="address-autocomplete__input-wrap">
        <IonIcon icon={locationOutline} className="address-autocomplete__icon" />
        <input
          ref={inputRef}
          type="text"
          className="address-autocomplete__input"
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => { isFocusedRef.current = true; if (suggestions.length > 0) { updateDropdownPos(); setIsOpen(true); } }}
          onBlur={() => { isFocusedRef.current = false; }}
          placeholder={placeholder}
          autoComplete="off"
        />
        {isLoading && <div className="address-autocomplete__spinner" />}
        {inputValue && !isLoading && (
          <button type="button" className="address-autocomplete__clear" onClick={handleClear}>
            <IonIcon icon={close} />
          </button>
        )}
      </div>
      {dropdown}
    </div>
  );
};

export default CityAutocomplete;
